import * as React from 'react';

import { translate } from '~l10n/client';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { toast } from '~shared/components/Toast';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useCopyWorkspaceQuizToPersonal } from '~teacher/hook/workspaces/useCopyWorkspaceQuizToPersonal';

import { WorkspaceQuiz } from '../../../../../api/workspaces/quiz/types';

export function useCopyQuizToPersonalAction(
  workspaceId: string
): Action<WorkspaceQuiz> {
  const {
    permissions: { copyQuizToPersonal: canCopyQuizToPersonal },
  } = useWorkspacePermissionsV2({ workspaceId });
  const copyQuizToPersonalMutation = useCopyWorkspaceQuizToPersonal();

  const handleCopyQuizToPersonal = React.useCallback(
    ({ data, close }: { data: WorkspaceQuiz[]; close: () => void }) => {
      const workspaceQuizIds = data.map((workspaceQuiz) => workspaceQuiz.id);

      const workspaceId = data[0].workspaceId;
      copyQuizToPersonalMutation.mutate(
        { workspaceId, workspaceQuizIds },
        {
          onSuccess: () => {
            toast.positive(translate('Copied'));

            new CreateQuizEventAdapter({
              creation_method: 'from_workspace',
              source_workspace_id: workspaceId,
              number_of_quizzes: data.length,
            }).sendAnalytics();
          },
          onError: () => {
            toast.critical(translate('An error occurred while trying to copy'));
          },
        }
      );
      close();
    },
    [copyQuizToPersonalMutation]
  );

  return React.useMemo<Action<WorkspaceQuiz>>(
    () => ({
      key: 'copy-to-personal',
      title: translate('Copy to Personal'),
      action: handleCopyQuizToPersonal,
      constraint: ({ data }) => data.every((quiz) => quiz.type === 'quiz'),
      disabled: () => !canCopyQuizToPersonal,
      showProBadge: !canCopyQuizToPersonal,
    }),
    [canCopyQuizToPersonal, handleCopyQuizToPersonal]
  );
}
